import "../../editorStyleSheet.css"
import PropTypes from "prop-types";
import React, {useCallback, useEffect, useState} from 'react';
import {getScriptDirection} from "../../../../utlis/generalUtils";
import {useSoftUIController} from "../../../../context";
import SaveButton from "../editorToolbarComponents/SaveButton";
import CircularProgress from '@mui/material/CircularProgress';
import AudioPlayer from "../wavesurfer/AudioPlayer";
import { EditText } from 'react-edit-text';
import 'react-edit-text/dist/index.css';
import {Box, Button, ButtonGroup, IconButton, Tooltip, useMediaQuery, useTheme} from "@mui/material";
import {removeDiacriticsFromWords} from "../../../../axios-client";
import { useTranslation } from "react-i18next";
import RemoveDiacritics from "../../../../reusableComponents/Icons/RemoveDiacritics"
import FontSize from "../editorToolbarComponents/editorToolbarElements/FontSize";
import RegenerateServicesPopup from "../editorToolbarComponents/RegenerateServicesPopup";
import {FileText, Languages, Redo2, Undo2} from "lucide-react";
import ChatBot from "../../../chatbot/ChatBot";

const DigitizationContentComponent = ({
    containerWidth, configWidth, pageNumber, updateConfigWidth,
    page, sourceLanguage, targetLanguage, addOrUpdatePages, updatedPages, handleSave, regenerateBookmarking, bookmarkingSelected,
    translationSelected, diacriticsSelected, articleId, regenerateAudio, isProcessing, numPages, fileName, fetchData, digitizationId,
    selectedWordBbox, setSelectedWordBbox, setPdfDimensions
                                      }) => {
    const [controller] = useSoftUIController();
    const {role} = controller;
    const {i18n} = useTranslation();
    const { t } = useTranslation(['editor']);
    const dir = i18n.dir();
    const [displayExtractedText, setDisplayExtractedText] = useState(true);
    const [textContent, setTextContent] = useState('');
    const [translatedContent, setTranslatedContent] = useState('');
    const [fontSize, setFontSize] = useState(16);
    const [vertices, setVertices] = useState({});
    const [loading, setLoading] = useState(false);
    const [audioUrl, setAudioUrl] = useState('');
    const noContentTags = ["No OCR is available.", "No Translation is available.", "NO-TEXT due to harmful content."];

    const [pageHistories, setPageHistories] = useState({});
    const currentPageHistory = pageHistories[pageNumber] || { history: [], index: -1 };
    const canUndo = currentPageHistory.index > 0;
    const canRedo = currentPageHistory.index < currentPageHistory.history.length - 1;

    const [translatedHistories, setTranslatedHistories] = useState({});
    const currentTranslatedHistory = translatedHistories[pageNumber] || { history: [], index: -1 };
    const canUndoTranslated = currentTranslatedHistory.index > 0;
    const canRedoTranslated = currentTranslatedHistory.index < currentTranslatedHistory.history.length - 1;
    const isRtl = dir === "rtl" ;

    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.down("md"));
    const [isFocused, setIsFocused] = useState(false);

    const pushSnapshot = useCallback((newVertices) => {
        const snapshot = {
            vertices: JSON.parse(JSON.stringify(newVertices))
        };
        setPageHistories(prev => {
            const current = prev[pageNumber] || { history: [], index: -1 };
            const history = current.history;
            // if the index is not the last, we add a new indexed history
            if (current.index != history.length - 1) {
                let duplicateIndex = current.history[current.index]
                history.push(duplicateIndex);
            }
            history.push(snapshot);
            return {
                ...prev,
                [pageNumber]: { history, index: history.length - 1 },
            };
        });
    }, [vertices]);

    const pushInitialSnapshot = useCallback((newVertices) => {
         const initialSnapshot = {
             vertices: JSON.parse(JSON.stringify(newVertices))
         };
         setPageHistories(prev => {
             if (!prev[pageNumber]) {
                 return {
                     ...prev,
                     [pageNumber]: { history: [initialSnapshot], index: 0 },
                 };
             }
             return prev;
         });
     }, [pageNumber]);

    const handleUndo = useCallback(() => {
        setPageHistories(prev => {
            const current = prev[pageNumber];
            if (!current || current.index < -1) return prev;
            const newIndex = current.index - 1;
            const snapshot = current.history[newIndex];
            setVertices(JSON.parse(JSON.stringify(snapshot.vertices)));
            addOrUpdatePages(pageNumber, JSON.stringify(snapshot.vertices), 'vertices');
            setTextContent(snapshot.textContent);
            return {
                ...prev,
                [pageNumber]: { ...current, index: newIndex },
            };
        });
    }, [pageNumber]);

    const handleRedo = useCallback(() => {
        setPageHistories(prev => {
            const current = prev[pageNumber];
            if (!current || current.index >= current.history.length - 1) return prev;
            const newIndex = current.index + 1;
            const snapshot = current.history[newIndex];
            setVertices(JSON.parse(JSON.stringify(snapshot.vertices)));
            setTextContent(snapshot.textContent);
            addOrUpdatePages(pageNumber, JSON.stringify(snapshot.vertices), 'vertices');
            return {
                ...prev,
                [pageNumber]: { ...current, index: newIndex },
            };
        });
    }, [pageNumber]);


    useEffect(() => {
        if (page && page.vertices) {
            checkPageType(page);
            let verticesJsonFormat = JSON.parse(page.vertices);
            setPdfDimensions({width: verticesJsonFormat.width, height: verticesJsonFormat.height})
            setAudioUrl(page.audio_path);
            setVertices(verticesJsonFormat);
            pushInitialSnapshot(verticesJsonFormat);
            setTextContent(page.content ?? "");
        }

    }, [page]);

    useEffect(() => {
        if (page) {
            setTranslatedHistories(prev => {
                const current = prev[pageNumber];
                if (current) {

                    setTranslatedContent(current.history[current.index]);
                    return prev;
                }

                const initialTranslated = page.translated_text ?? "";
                setTranslatedContent(initialTranslated);
                return {
                    ...prev,
                    [pageNumber]: {
                        history: [initialTranslated],
                        index: 0
                    }
                };
            });
        }
    }, [page]);

    const handleUndoTranslated = () => {
        setTranslatedHistories(prev => {
            const current = prev[pageNumber];
            if (!current || current.index <= 0) return prev;
            const newIndex = current.index - 1;
            setTranslatedContent(current.history[newIndex]);
            addOrUpdatePages(pageNumber, current.history[newIndex], 'translated_text');
            return {
                ...prev,
                [pageNumber]: { ...current, index: newIndex },
            };
        });
    };

    const handleRedoTranslated = () => {
        setTranslatedHistories(prev => {
            const current = prev[pageNumber];
            if (!current || current.index >= current.history.length - 1) return prev;
            const newIndex = current.index + 1;
            setTranslatedContent(current.history[newIndex]);
            addOrUpdatePages(pageNumber, current.history[newIndex], 'translated_text');
            return {
                ...prev,
                [pageNumber]: { ...current, index: newIndex },
            };
        });
    };



    useEffect(() => {
        const handleKeyDown = (evt) => {
            if ((evt.ctrlKey || evt.metaKey) && evt.key.toLowerCase() === 'z') {
                evt.preventDefault();
                evt.stopPropagation();
                displayExtractedText ? handleUndo() : handleUndoTranslated();
            } else if ((evt.ctrlKey || evt.metaKey) && evt.key.toLowerCase() === 'y') {
                evt.preventDefault();
                evt.stopPropagation();
                displayExtractedText ? handleRedo() : handleRedoTranslated();
            }
        };
        window.addEventListener('keydown', handleKeyDown, true);
        return () => {
            window.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [displayExtractedText, handleUndo, handleRedo, handleUndoTranslated, handleRedoTranslated]);


    const checkPageType = (currentPage) => {
        switch (currentPage.type) {
            case "NO_TEXT":
                setTextContent("No TEXT.");
                setTranslatedContent("No TEXT.");
                break;
            case "HARMFUL_CONTENT":
                setTextContent(currentPage.content ?? "No OCR is available.")
                setTranslatedContent("NO-TEXT due to harmful content.");
                break;
            case "TEXT":
                setTextContent(currentPage.content ?? "No OCR is available.")
                setTranslatedContent(currentPage.translated_text ?? "No Translation is available.");
                break;
            default:
                setTextContent(currentPage.content ?? "No OCR is available.")
                setTranslatedContent(currentPage.translated_text ?? "No Translation is available.");
        }
    }


    const handleSwitchChange = (value) => {
        setDisplayExtractedText(value);
    };

    const updateText = (e) => {
        if (page.type === 'TEXT' || page.type === null) {
            setTranslatedContent(e.target.value);
            addOrUpdatePages(pageNumber, e.target.value, 'translated_text');
            setTranslatedHistories(prev => {
                const current = prev[pageNumber] || { history: [], index: -1 };
                const newHistory = [...current.history.slice(0, current.index + 1), e.target.value];
                return {
                    ...prev,
                    [pageNumber]: {
                        history: newHistory,
                        index: newHistory.length - 1
                    }
                };
            });
        }
    }

    const handleContentSectionMouseDown = useCallback((e) => {
        e.preventDefault();
        const initialWidth = e.target.parentElement.offsetWidth;
        const initialDivWidth = e.target.offsetParent.offsetWidth;
        const initialX = e.clientX;

        const handleMouseContentSectionMove = (e) => {
            let newWidth = initialWidth - (e.clientX - initialX);

            if (newWidth < initialDivWidth * 0.2) {
                newWidth = (initialDivWidth * 0.2);
            } else if (newWidth > initialDivWidth * 0.75) {
                newWidth = (initialDivWidth * 0.75);
            }

            const contentSection = document.getElementById("digitizationEditorContentSection");
            if (contentSection && contentSection.offsetWidth !== newWidth) {
                contentSection.style.width = newWidth + 'px';
                updateConfigWidth(newWidth);
            }
            const event = new CustomEvent('configResized');
            window.dispatchEvent(event);
        };

        const handleMouseContentSectionUp = () => {
            document.removeEventListener('mousemove', handleMouseContentSectionMove);
            document.removeEventListener('mouseup', handleMouseContentSectionUp);
        };

        document.addEventListener('mousemove', handleMouseContentSectionMove);
        document.addEventListener('mouseup', handleMouseContentSectionUp);
    }, [updateConfigWidth]);

    const updateWords = (event, line, word) => {
        const oldValue = vertices.lines[line].words[word].word_text;
        const newValue = event.target.value;
        if (oldValue === newValue) return;
        let newVertices = JSON.parse(JSON.stringify(vertices));
        newVertices.lines[line].words[word].word_text = newValue;
        setVertices(newVertices);
        addOrUpdatePages(pageNumber, JSON.stringify(newVertices), 'vertices');
        pushSnapshot(newVertices);
    };


    const diacritizeWords = () => {
        setLoading(true);
        diacritizeDraft(vertices)
            .then((response) => {
                let newVertices = response.data.vertices;
                if (newVertices) {
                    //pushSnapshot();
                    setVertices(newVertices)
                    addOrUpdatePages(pageNumber, JSON.stringify(newVertices), 'vertices');
                }
            })
            .catch(e => {
                console.log('error', e)
            })
            .finally(() => {
                setLoading(false);
            });
    }

    const removeDiacritics = () => {
        setLoading(true);
        removeDiacriticsFromWords(vertices)
            .then((response) => {
                let newVertices = response.data.vertices;
                if (newVertices) {
                    setVertices(newVertices)
                    addOrUpdatePages(pageNumber, JSON.stringify(newVertices), 'vertices');
                    setPage({...page, vertices: JSON.stringify(newVertices)})
                }
            })
            .catch(e => {
                console.log('error', e)
            })
            .finally(() => {
                setLoading(false);
            });
    }

    const handleSaveWithLoading = () => {
        setLoading(true);
        handleSave()
            .then(() => {
                // success handled in handleSave
            })
            .catch((err) => {
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <div className="digitizationEditorContentSection" id="digitizationEditorContentSection"
             style={{display: 'flex', flexDirection: 'column', height: '100%', maxHeight: '100vh'}}
        >
            <div className="resize-handle" onMouseDown={handleContentSectionMouseDown}/>
            <Box sx={{
                borderBottom: "1px solid #e0d8d8b0",
                px: {xs: 1, sm: 2},
                py: {xs: 0.5, sm: 1},
                height: {xs: "auto", sm: "3.5rem"},
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: {xs: "wrap", md: "nowrap"},
                gap: {xs: 1, sm: 2},
                minWidth: "max-content",
            }}
                 dir={dir}
            >


                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>

                    <FontSize
                        fontSize={fontSize}
                        setFontSize={setFontSize}
                    />

                    {displayExtractedText && (
                        <>
                            <Tooltip title="Undo (Ctrl+Z)">
                        <span>
                            <IconButton
                                variant="outlined"
                                size={isMobile ? "small" : "medium"}
                                sx={{
                                    width: {xs: 28, sm: 32},
                                    height: {xs: 28, sm: 32},
                                    fontSize: {xs: "0.65rem", sm: "0.75rem"},
                                    color: "#616161",
                                    border: "1px solid #d8d6d6",
                                    borderRadius: "5px",
                                    p: 1,
                                    "&:hover": {
                                        borderColor: "#757575",
                                        background: "#F5F5F5",
                                    },
                                }}
                                onClick={handleUndo} disabled={!canUndo}>
                              <Undo2 size={isMobile ? 14 : 16}/>
                            </IconButton>
                        </span>
                            </Tooltip>

                            <Tooltip title="Redo (Ctrl+Y)">
                        <span>
                            <IconButton
                                variant="outlined"
                                size={isMobile ? "small" : "medium"}
                                sx={{
                                    width: {xs: 28, sm: 32},
                                    height: {xs: 28, sm: 32},
                                    fontSize: {xs: "0.65rem", sm: "0.75rem"},
                                    color: "#616161",
                                    border: "1px solid #d8d6d6",
                                    borderRadius: "5px",
                                    p: 1,
                                    "&:hover": {
                                        borderColor: "#757575",
                                        background: "#F5F5F5",
                                    },
                                }}
                                onClick={handleRedo} disabled={!canRedo}>
                            <Redo2 size={isMobile ? 14 : 16}/>
                            </IconButton>
                        </span>
                            </Tooltip>
                        </>
                    )}

                    {!displayExtractedText && (
                        <>
                            <Tooltip title="Undo Translation (Ctrl+Z)">
                                <span>
                                    <IconButton
                                        variant="outlined"
                                        size={isMobile ? "small" : "medium"}
                                        sx={{
                                            width: {xs: 28, sm: 32},
                                            height: {xs: 28, sm: 32},
                                            fontSize: {xs: "0.65rem", sm: "0.75rem"},
                                            color: "#616161",
                                            border: "1px solid #d8d6d6",
                                            borderRadius: "5px",
                                            p: 1,
                                            "&:hover": {
                                                borderColor: "#757575",
                                                background: "#F5F5F5",
                                            },
                                        }}
                                        onClick={handleUndoTranslated} disabled={!canUndoTranslated}>
                                        <Undo2 size={isMobile ? 14 : 16}/>
                                    </IconButton>
                                </span>
                            </Tooltip>

                            <Tooltip title="Redo Translation (Ctrl+Y)">
                                <span>
                                    <IconButton
                                        variant="outlined"
                                        size={isMobile ? "small" : "medium"}
                                        sx={{
                                            width: {xs: 28, sm: 32},
                                            height: {xs: 28, sm: 32},
                                            fontSize: {xs: "0.65rem", sm: "0.75rem"},
                                            color: "#616161",
                                            border: "1px solid #d8d6d6",
                                            borderRadius: "5px",
                                            p: 1,
                                            "&:hover": {
                                                borderColor: "#757575",
                                                background: "#F5F5F5",
                                            },
                                        }}
                                        onClick={handleRedoTranslated} disabled={!canRedoTranslated}>
                                        <Redo2 size={isMobile ? 14 : 16}/>
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </>
                    )}

                </Box>

                <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                    <ButtonGroup
                        variant="outlined"
                        disableElevation
                        sx={{
                            bgcolor: "#ffffff !important",
                            "& .MuiButton-root": {
                                height: 32,
                                fontSize: "0.75rem",
                                color: "#616161", // grey.700
                                borderColor: "#bdbdbd !important", // grey.400
                                "&:hover": {
                                    borderColor: "#757575 !important", // grey.600
                                    bgcolor: "#f5f5f5 !important", // grey.100
                                },
                                "&.Mui-focusVisible, &:focus": {
                                    outline: "none !important",
                                    boxShadow: "none !important",
                                },
                            },
                            "& .MuiButton-contained": {
                                bgcolor: "#424242 !important", // grey.800
                                color: "#ffffff",
                                "&:hover": {
                                    bgcolor: "#212121 !important",
                                    borderColor: "#bdbdbd !important",// grey.900
                                },
                            },
                            "&.Mui-focusVisible, &:focus": {
                                bgcolor: "#212121 !important",
                                borderColor: "#bdbdbd !important",
                                outline: "none !important",
                                boxShadow: "none !important",
                            },
                            "& .MuiButtonGroup-firstButton": {
                                borderTopLeftRadius: isRtl ? 0 : 4,
                                borderBottomLeftRadius: isRtl ? 0 : 4,
                                borderTopRightRadius: isRtl ? 4 : 0,
                                borderBottomRightRadius: isRtl ? 4 : 0,
                            },
                            "& .MuiButtonGroup-lastButton": {
                                borderTopRightRadius: isRtl ? 0 : 4,
                                borderBottomRightRadius: isRtl ? 0 : 4,
                                borderTopLeftRadius: isRtl ? 4 : 0,
                                borderBottomLeftRadius: isRtl ? 4 : 0,
                            },
                        }}
                    >

                        <Button
                            variant={displayExtractedText ? "contained" : "outlined"}
                            onClick={() => handleSwitchChange(true)}
                            startIcon={<FileText size={isMobile ? 10 : 12}/>}
                            sx={{
                                fontSize: {xs: "0.65rem", sm: "0.75rem"},
                                px: {xs: 1, sm: 2},
                                minWidth: {xs: 32, sm: "auto"},
                                "& .MuiButton-startIcon": {
                                    marginLeft: dir === "rtl" ? "0.3rem" : "-4px",
                                    marginRight: dir === "rtl" ? "0px" : "8px"
                                },
                            }}
                        >
                            {isMobile ? "E" : t("extracted_text")}
                        </Button>

                        {translationSelected && (
                            <Button
                                variant={!displayExtractedText ? "contained" : "outlined"}
                                onClick={() => handleSwitchChange(false)}
                                startIcon={<Languages size={isMobile ? 10 : 12}/>}
                                sx={{
                                    fontSize: {xs: "0.65rem", sm: "0.75rem"},
                                    px: {xs: 1, sm: 2},
                                    minWidth: {xs: 32, sm: "auto"},
                                    "& .MuiButton-startIcon": {
                                        marginLeft: dir === "rtl" ? "0.3rem" : "-4px",
                                        marginRight: dir === "rtl" ? "0px" : "8px"
                                    },
                                }}
                            >
                                {isMobile ? "T" : t("translated_text")}
                            </Button>
                        )}
                    </ButtonGroup>
                </Box>

                <React.Fragment>
                    <Box sx={{display: "flex", alignItems: "center", gap: 1}}>

                        <RegenerateServicesPopup digitizationId={digitizationId} currentPageNum={pageNumber}
                                                 numPages={numPages}/>

                        <SaveButton updatedPages={updatedPages} handleSave={handleSaveWithLoading} disabled={loading}
                                    t={t} dir={dir}/>
                        {/*TODO will be reactivated when cover the payment for them*/}
                        {/*<Tooltip title="Re-generate bookmarking">*/}
                        {/*    <span>*/}
                        {/*        <IconButton*/}
                        {/*            onClick={regenerateBookmarking}*/}
                        {/*            disabled={!bookmarkingSelected}*/}
                        {/*        >*/}
                        {/*            <BsBookmarkCheckFill/>*/}
                        {/*        </IconButton>*/}
                        {/*    </span>*/}
                        {/*</Tooltip>*/}
                        {/*{*/}
                        {/*    sourceLanguage == 'Arabic' && <Tooltip title="Add Diacritics">*/}
                        {/*    <span>*/}
                        {/*        <IconButton*/}
                        {/*            onClick={diacritizeWords}*/}
                        {/*        >*/}
                        {/*            <CgDesignmodo />*/}
                        {/*        </IconButton>*/}
                        {/*    </span>*/}
                        {/*</Tooltip>*/}
                        {/*}*/}
                        {
                            sourceLanguage == 'Arabic' && diacriticsSelected && <Tooltip title={t('remove_diacritics')}>
                            <span>
                                <IconButton
                                    variant="outlined"
                                    size={isMobile ? "small" : "medium"}
                                    sx={{
                                        width: {xs: 28, sm: 32},
                                        height: {xs: 28, sm: 32},
                                        fontSize: {xs: "0.65rem", sm: "1.3rem"},
                                        color: "#616161",
                                        border: "1px solid #d8d6d6",
                                        borderRadius: "5px",
                                        padding: "0px !important",
                                        "&:hover": {
                                            borderColor: "#757575",
                                            background: "#F5F5F5",
                                        },
                                    }}
                                    onClick={removeDiacritics}
                                >
                                    <RemoveDiacritics/>
                                </IconButton>
                            </span>
                            </Tooltip>
                        }
                    </Box>

                </React.Fragment>
            </Box>

            <div style={{padding: "0px 15px 0px 15px", width: "100%", flexGrow: 1, overflow:  displayExtractedText ? 'auto' : 'hidden',
                direction: displayExtractedText ? getScriptDirection(sourceLanguage) : getScriptDirection(targetLanguage),
                backgroundColor: "white"
                }}
            >
                {loading && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255,255,255,0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10
                    }}>
                        <CircularProgress/>
                    </div>
                )}

            <div style={{
                    minWidth: 'min-content'
                }}>
                    {displayExtractedText && vertices && vertices.lines ? vertices.lines.map((line, lineIndex) => {
                            return (
                                <div key={lineIndex} style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    whiteSpace: "normal",
                                    fontWeight: "500",
                                    fontFamily: "system-ui",
                                    color: "black",
                                    fontSize: fontSize + "px"
                                }}>
                                    {line.words.map((word, wordIndex) => {
                                        return (
                                            <div key={lineIndex + "-" + wordIndex}
                                                 style={{background: !isFocused && selectedWordBbox.wordIndex === wordIndex && selectedWordBbox.lineIndex === lineIndex ? 'rgb(32, 143, 213, 0.2)' : null}}
                                                 onFocus={() => {
                                                     setIsFocused(true)
                                                 }}
                                                 onClick={() => {
                                                     setSelectedWordBbox({
                                                         lineIndex: lineIndex,
                                                         wordIndex: wordIndex,
                                                         word: word,
                                                         vertices: {...vertices}
                                                     })
                                                 }}>
                                                <EditText
                                                    id={"word-" + lineIndex + "-" + wordIndex}
                                                    key={wordIndex}
                                                    value={word.word_text}
                                                    onChange={(event) => updateWords(event, lineIndex, wordIndex)}
                                                    onFocus={() => {
                                                        setIsFocused(true)
                                                    }}
                                                    style={{
                                                        border: 'none',
                                                        background: isFocused && selectedWordBbox.wordIndex === wordIndex && selectedWordBbox.lineIndex === lineIndex ? 'rgb(32, 143, 213, 0.2)' : null
                                                    }}
                                                />
                                            </div>
                                        )
                                    })}
                                </div>)
                    }) : null}
            </div>

                {displayExtractedText && textContent && !vertices &&
                    <textarea
                        disabled={loading}
                        onChange={!noContentTags.includes(translatedContent) ? updateText : undefined}
                        value={textContent}
                        className="digitization-note"
                        style={{
                            borderColor: '#e6e6e7',
                            borderRadius: '4px',
                            width: '100%',
                            padding: "15px",
                            direction: getScriptDirection(sourceLanguage),
                            minHeight: '90vh',
                            boxSizing: 'border-box',
                            overflowX: 'auto',
                            whiteSpace: 'nowrap',
                            fontWeight: "500",
                            fontFamily: "system-ui",
                            fontSize: fontSize + "px",
                            textAlign: "justify"
                        }}
                        placeholder="Text content will appear here."
                    />
                }

                {!displayExtractedText &&
                    <textarea
                        disabled={loading}
                        onChange={!noContentTags.includes(translatedContent) ? updateText : undefined}
                        value={translatedContent}
                        className="digitization-note"
                        style={{
                            borderColor: '#e6e6e7',
                            borderRadius: '4px',
                            fontWeight: "500",
                            fontFamily: "system-ui",
                            fontSize: fontSize + "px",
                            width: '100%',
                            minHeight: '70vh',
                            padding: "15px",
                            direction: getScriptDirection(targetLanguage),
                            height: '100%',
                            boxSizing: 'border-box',
                            overflowX: 'auto',
                            whiteSpace: 'pre-wrap',
                            textAlign: "justify"
                        }}
                        placeholder="Text content will appear here."
                    />
                }
            </div>
            {displayExtractedText && audioUrl && !loading && (
                <AudioPlayer audioUrl={audioUrl} articleId={articleId} regenerateAudio={regenerateAudio} isRegenerating={isProcessing}/>
            )}

            <ChatBot digitizationId={digitizationId} />
        </div>
    )
}

DigitizationContentComponent.propTypes = {
    containerWidth: PropTypes.string,
    pageNumber: PropTypes.number,
    articleId: PropTypes.number,
    digitizationId: PropTypes.number,
    configWidth: PropTypes.string,
    updateConfigWidth: PropTypes.func,
    page: PropTypes.object,
    sourceLanguage: PropTypes.string,
    targetLanguage: PropTypes.string,
    addOrUpdatePages: PropTypes.func,
    updatedPages: PropTypes.array,
    handleSave: PropTypes.func,
    bookmarkingSelected: PropTypes.bool,
    translationSelected: PropTypes.bool,
    diacriticsSelected: PropTypes.bool,
    diacritizeWords: PropTypes.func,
    regenerateBookmarking: PropTypes.func,
    regenerateAudio: PropTypes.func,
    isProcessing: PropTypes.bool,
    numPages: PropTypes.number,
    fileName: PropTypes.string,
    fetchData: PropTypes.func,
    selectedWordBbox: PropTypes.array,
    setSelectedWordBbox: PropTypes.func,
    setPdfDimensions: PropTypes.func
};

export default DigitizationContentComponent;
